import React, { useState, useEffect } from "react"
import Modal from "@/components/core/Modal/Modal"
import Button from "@/components/core/Button/Button"

import { useTranslation } from "next-i18next"
import {
  addMavenoidChat,
  preventBodyScroll,
  sanitizeInnerHtml,
} from "@/utils/helper"
import { useSelector } from "react-redux"
import { selectGenericState } from "@/store/features/genericSlice"
import {
  siteWideWarningModalCloseAnalytics,
  siteWideWarningModalCloseEventInfo,
} from "@/components/SitewideWarning/analytics"

const SitewideWarning = props => {
  const { enableSiteWideWarning } = useSelector(selectGenericState)
  const { siteWideWarning } = props
  const { t } = useTranslation("common")
  const [showModal, setShowModal] = useState(false)
  const [componentProps, setComponentProps] = useState({})
  const [dataLayer, setDataLayer] = useState()

  const isSocialShare = () =>
    window?.sitewideWarningEvent?.linkType === "social media"
  /**
   *
   * @param {*} shouldRedirect Boolean
   * Handle the redirect if the user select yes from the model
   */
  const handleRedirect = shouldRedirect => {
    const e = window.sitewideWarningEvent

    if (e && shouldRedirect) {
      if (e.target.target === "_blank" || e.target === "_blank") {
        window.open(e.target.href ?? e.href)
      } else {
        window.location = e.target.href ?? e.href
      }
    }

    if (isSocialShare()) {
      const eventInfo = siteWideWarningModalCloseEventInfo({
        internalLinkName: (shouldRedirect
          ? t("kf.label.sitewide.warning.yesCta")
          : t("kf.label.sitewide.warning.noCta")
        )?.toLowerCase(),
        internalLinkURL: shouldRedirect ? e.target.href || e.href : "n/a",
        clickInternalLinks: shouldRedirect ? "false" : "true",
      })
      siteWideWarningModalCloseAnalytics(eventInfo)
    }

    window.showSiteWideWarning(false)
    window.sitewideWarningEvent = null
  }
  /**
   *
   * @param {*} url String (  the url to be added to the model)
   * @param {*} target String (  the target)
   * This will handle the model content and map the url to be redirected based on user selection
   *
   */
  const handleShowModal = (url, target) => {
    const e = {
      target: "_blank",
      href: url,
      linkType: target?.getAttribute("data-link-type") ?? "",
    }
    window.sitewideWarningEvent = e
    window.showSiteWideWarning(true)
  }

  useEffect(() => {
    window.showSiteWideWarning = setShowModal

    const authorData = siteWideWarning ? siteWideWarning : {}
    const excludUrl = authorData?.popupIgnoreUrls
      ? authorData?.popupIgnoreUrls.map(item => item.url).toString()
      : ""

    document
      .querySelectorAll(
        'a[href^="http"]:not([data-siteWideWarning-added="true"])'
      )
      .forEach(link => {
        const nodeListUrl = link.hostname.replace(/(^www\.)/, "")
        link.setAttribute("data-siteWideWarning-added", true)
        if (!excludUrl.includes(nodeListUrl)) {
          link.onclick = function (e) {
            e.stopPropagation()
            e.preventDefault()
            triggerAnalyticsPart(this)
            const event = {
              target: this?.target,
              href: this?.href,
              linkType: this?.getAttribute("data-link-type") ?? "",
            }
            window.sitewideWarningEvent = event
            window.showSiteWideWarning(true)
          }
        }
      })
    document
      .querySelectorAll(
        'a[data-gbh-data-layer]:not([href^="http"]):not(.gbh-data-layer):not([data-gbh-clickable=true])'
      )
      .forEach(link => {
        link.onclick = function (e) {
          triggerAnalyticsPart(this)
        }
      })

    const triggerAnalyticsPart = obj => {
      let gbhDataLayer = obj.getAttribute("data-gbh-data-layer")
      const gbhDataLayerCustom = obj.getAttribute("data-gbh-data-layer-custom")
      const gbhDataLayerCustomParse = JSON.parse(gbhDataLayerCustom)

      const productInfo =
        gbhDataLayerCustomParse && gbhDataLayerCustomParse.productInfo
          ? gbhDataLayerCustomParse.productInfo
          : undefined

      if (!gbhDataLayer) {
        const pageInfoDetails = window?.adobeDataLayer?.getState("page")
        const parentDiv = obj.closest(".cmp-teaser__description")
        if (parentDiv) {
          gbhDataLayer = parentDiv.getAttribute("data-gbh-data-layer")
          const teaserDescriptionJson = JSON.parse(gbhDataLayer)
          const eventActionMessage =
            teaserDescriptionJson.eventAction.split(":")
          if (
            pageInfoDetails?.pageInfo?.contentType &&
            pageInfoDetails?.pageInfo?.contentType === "pdp"
          ) {
            const isPdpEvent = `${pageInfoDetails?.pageInfo?.contentType}:${
              eventActionMessage[1]
            }:${obj?.innerText?.toLowerCase()}`
            teaserDescriptionJson.eventAction = isPdpEvent
            teaserDescriptionJson.eventName = isPdpEvent
            teaserDescriptionJson.eventType = "navigation"
            teaserDescriptionJson.internalLinkName =
              obj?.innerText?.toLowerCase()
            teaserDescriptionJson.internalLinkPosition = "pdp"
            teaserDescriptionJson.internalLinkName =
              obj?.innerText?.toLowerCase()
            teaserDescriptionJson.internalLinkZoneName = "pdp:teaser"
            teaserDescriptionJson.internalLinkZoneName = `pdp:${eventActionMessage[1]}`
          }
          teaserDescriptionJson.internalLinkURL = obj?.href
          const targetAnchorTag = obj.getAttribute("target")
          if (targetAnchorTag === "_blank") {
            teaserDescriptionJson.clickInternalLinks = "false"
          }
          gbhDataLayer = JSON.stringify(teaserDescriptionJson)
        }
      }
      if (gbhDataLayer) {
        setDataLayer(gbhDataLayer)
        const { adobeDataLayer: { getState } = {} } = window
        const page = window?.pageObj || (getState && getState("page")) || {}
        const regex = new RegExp("(<([^>]+)>)", "ig")
        const jsonString = gbhDataLayer
          .replaceAll(regex, "")
          .replaceAll(/(\r\n|\n|\r)/gm, "")
        if (!obj.getAttribute("data-gbh-clickable")) {
          window.adobeDataLayer.push({
            event: "cmp:click",
            eventInfo: JSON.parse(jsonString),
            page,
            ...(productInfo ? { productInfo } : {}),
          })
        }
      }
    }
    /**
     * Populating the popup valur to be shown in the page
     */
    const popupVal = {
      title:
        authorData && authorData.popupMessageTitle
          ? authorData.popupMessageTitle
          : t("kf.label.sitewide.warning.modalWindow"),
      description:
        authorData && authorData.popupMessage
          ? authorData.popupMessage
          : t("kf.label.sitewide.warning.desc"),
    }
    setComponentProps(popupVal)
    window.handleShowModal = handleShowModal
    window.excludUrl = excludUrl
    window.showSiteWideWarningRendered = true
  }, [enableSiteWideWarning])

  useEffect(() => {
    if (showModal) {
      preventBodyScroll(true)
    }

    return () => {
      preventBodyScroll(false)
    }
  }, [showModal])

  return (
    <Modal
      className="site-warning-part-diagram"
      showModal={showModal}
      onModalClose={() => {
        addMavenoidChat()
        window.showSiteWideWarning(false)
      }}
      needAnalyticsOnContentsLink={true}
      dataLayer={dataLayer}
      modalTitle={componentProps.title}
      {...(isSocialShare()
        ? {
            closeButtonAnalytics: siteWideWarningModalCloseEventInfo({
              internalLinkName: "close",
              internalLinkURL: "n/a",
              returnString: true,
              clickInternalLinks: "true",
            }),
          }
        : {})}
    >
      <div
        className="sitewide-warning"
        aria-labelledby="sitewide-warning__title"
        aria-describedby="sitewide-warning__desc"
        data-testid="sitewidewarning-model"
      >
        <h3 className="sitewide-warning__title" id="sitewide-warning__title">
          {componentProps.title}
        </h3>
        <div className="sitewide-warning__desc" id="sitewide-warning__desc">
          {sanitizeInnerHtml(componentProps.description)}
        </div>
        <Button
          role="button"
          onClick={() => handleRedirect(true)}
          label={t("kf.label.sitewide.warning.yesCta")}
          flexible={true}
          useDefaultClass={true}
        />
        <Button
          role="button"
          onClick={() => handleRedirect(false)}
          label={t("kf.label.sitewide.warning.noCta")}
          type="ghost"
          flexible={true}
        />
      </div>
    </Modal>
  )
}

export default SitewideWarning
